import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';

/**
 * ClientsPage Component
 * Extracted from App.js for better code organization.
 * Handles client list and client form drawer.
 */
const ClientsPage = ({
  // Data
  clients,
  appointments,
  // Form State
  showClientForm,
  editingClient,
  clientForm,
  clientFormActiveTab,
  clientAvatarUrls,
  uploadingCompanyLogo,
  // Setters
  setShowClientForm,
  setEditingClient,
  setClientForm,
  setClientFormActiveTab,
  setActiveTab,
  setSelectedListingForEdit,
  setPropertyClients,
  setShowAppointmentForm,
  setEditingAppointment,
  setAppointmentForm,
  setSelectedProducts,
  setAppointmentClientSearchQuery,
  // Functions
  hasPermission,
  handleClientSubmit,
  resetClientForm,
  fetchAvatarBlob,
  fetchAppointmentMedia,
  deleteClient
}) => {
  const safeClients = Array.isArray(clients) ? clients : [];
  const safeAppointments = Array.isArray(appointments) ? appointments : [];
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Reset the delete-confirmation when the user switches between clients or
  // closes the form — otherwise the modal stays open across client switches.
  useEffect(() => {
    setShowDeleteConfirm(false);
  }, [editingClient?.id, showClientForm]);

  const handleEditClient = (client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      notes: client.notes || '',
      avatar: null,
      companyLogo: null,
      removeAvatar: false,
      removeCompanyLogo: false
    });
    setClientFormActiveTab('profile');
    setShowClientForm(true);
  };

  const handleRowClick = (client) => {
    handleEditClient(client);
  };

  const handleOpenNewClient = () => {
    setEditingClient(null);
    setClientForm({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      avatar: null,
      companyLogo: null,
      removeAvatar: false,
      removeCompanyLogo: false
    });
    setClientFormActiveTab('profile');
    setShowClientForm(true);
  };

  const handleDrawerClose = (open) => {
    if (!open) {
      resetClientForm();
      setShowClientForm(false);
    }
  };

  const getClientListings = (clientId) => {
    return safeAppointments.filter(apt => 
      apt.client_id === clientId || (apt.client_ids && apt.client_ids.includes(clientId))
    );
  };

  return (
    <div className="space-y-6 w-full px-6 md:px-8 lg:px-12">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Clients</h2>
        {hasPermission('clients', 'create') && (
          <button
            onClick={handleOpenNewClient}
            className="clara-btn-primary px-6 py-3"
            data-testid="add-client-btn"
          >
            + Add Client
          </button>
        )}
      </div>

      {/* Client Drawer - slides in from left */}
      <Sheet open={showClientForm} onOpenChange={handleDrawerClose}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl md:max-w-2xl overflow-y-auto p-0"
          data-testid="client-drawer"
        >
          <SheetHeader className="p-6 pb-0">
            {editingClient ? (
              <div className="flex items-center gap-4" data-testid="client-drawer-title">
                {(() => {
                  const avatarUrl = editingClient.avatar ? clientAvatarUrls[editingClient.avatar] : null;
                  return avatarUrl ? (
                    <img
                      src={avatarUrl}
                      alt={editingClient.name}
                      className="w-16 h-16 rounded-full object-cover ring-2 ring-gray-100"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-semibold ring-2 ring-gray-100">
                      {editingClient.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                  );
                })()}
                <SheetTitle className="text-2xl">{editingClient.name}</SheetTitle>
              </div>
            ) : (
              <SheetTitle data-testid="client-drawer-title" className="text-2xl">Add New Client</SheetTitle>
            )}
            <SheetDescription className="sr-only">
              {editingClient ? `Details for ${editingClient.name}` : 'Create a new client'}
            </SheetDescription>
          </SheetHeader>

          {/* Tab Navigation */}
          <div className="px-6 pt-4">
            <div className="flex space-x-1 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setClientFormActiveTab('profile')}
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  clientFormActiveTab === 'profile'
                    ? 'border-blue-500 text-blue-500'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                data-testid="client-tab-profile"
              >
                Profile
              </button>
              {editingClient && (
                <button
                  type="button"
                  onClick={() => setClientFormActiveTab('listings')}
                  className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                    clientFormActiveTab === 'listings'
                      ? 'border-blue-500 text-blue-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  data-testid="client-tab-listings"
                >
                  Listings
                </button>
              )}
            </div>
          </div>
            
          {/* Profile Tab Content */}
          {clientFormActiveTab === 'profile' && (
            <form onSubmit={handleClientSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  required
                  value={clientForm.name}
                  onChange={(e) => setClientForm({...clientForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="client-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({...clientForm, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="client-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                <input
                  type="tel"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({...clientForm, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="client-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                <input
                  type="text"
                  value={clientForm.company}
                  onChange={(e) => setClientForm({...clientForm, company: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="client-company-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={clientForm.notes}
                  onChange={(e) => setClientForm({...clientForm, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  data-testid="client-notes-input"
                />
              </div>

              {/* Avatar Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Avatar</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setClientForm({...clientForm, avatar: e.target.files[0], removeAvatar: false});
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="client-avatar-input"
                />
                {editingClient?.avatar && !clientForm.removeAvatar && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-gray-500">Current avatar set</span>
                    <button
                      type="button"
                      onClick={() => setClientForm({...clientForm, removeAvatar: true})}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Company Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files?.[0]) {
                      setClientForm({...clientForm, companyLogo: e.target.files[0], removeCompanyLogo: false});
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  data-testid="client-logo-input"
                />
                {uploadingCompanyLogo && (
                  <div className="mt-2 text-sm text-blue-600">Uploading logo...</div>
                )}
              </div>

              {/* Form Actions */}
              <div className="flex items-center justify-between gap-3 pt-4 border-t border-gray-200">
                {editingClient && hasPermission && hasPermission('clients', 'delete') !== false ? (
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(true)}
                    data-testid="delete-client-btn"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    Delete Client
                  </button>
                ) : <span />}
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      resetClientForm();
                      setShowClientForm(false);
                    }}
                    className="px-4 py-2 text-gray-700 hover:text-gray-900"
                    data-testid="cancel-client-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="clara-btn-primary px-6 py-2"
                    data-testid="save-client-btn"
                  >
                    {editingClient ? 'Update Client' : 'Create Client'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Listings Tab Content */}
          {clientFormActiveTab === 'listings' && editingClient && (
            <div className="p-6">
              <h4 className="font-medium text-gray-900 mb-4">Associated Listings</h4>
              {getClientListings(editingClient.id).length === 0 ? (
                <p className="text-gray-500 text-sm">No listings associated with this client.</p>
              ) : (
                <div className="space-y-3">
                  {getClientListings(editingClient.id).map(apt => (
                    <div
                      key={apt.id}
                      onClick={async () => {
                        setSelectedListingForEdit(apt);
                        setPropertyClients(apt.client_ids || []);
                        await fetchAppointmentMedia(apt.id);
                        setClientFormActiveTab('profile');
                        setShowClientForm(false);
                        setActiveTab('edit-listing', { listingId: apt.id });
                      }}
                      className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      data-testid={`client-listing-${apt.id}`}
                    >
                      <div className="font-medium text-gray-900">{apt.property_address}</div>
                      <div className="text-sm text-gray-500">{apt.scheduled_date}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Delete Client Confirmation Modal
              IMPORTANT: rendered INSIDE the SheetContent so it inherits the
              Radix Dialog's `pointer-events: auto` scope. If rendered as a
              sibling of <Sheet/> in the React tree, the Radix overlay blocks
              clicks on this modal and they fall through to the file inputs
              behind it. */}
          {showDeleteConfirm && editingClient && (
            <div
              data-testid="delete-client-confirm-modal"
              className="fixed inset-0 z-[60] flex items-center justify-center p-4"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
              onClick={() => setShowDeleteConfirm(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="bg-red-50 border-b border-red-100 px-6 py-4 flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/15 flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-red-900">Delete {editingClient.name}?</h3>
                    <p className="text-xs text-red-700 mt-0.5">This action cannot be undone.</p>
                  </div>
                </div>
                <div className="px-6 py-5 space-y-3">
                  <p className="text-sm text-gray-700 leading-relaxed">
                    The following data will be permanently removed or unlinked when you delete this client:
                  </p>
                  {(() => {
                    const cListings = getClientListings(editingClient.id);
                    const completed = cListings.filter((apt) => apt.status === 'completed').length;
                    const upcoming = cListings.filter((apt) => apt.status !== 'completed' && apt.status !== 'cancelled').length;
                    return (
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a4 4 0 014-4h4M19 4v16M5 4v16M5 4h4a4 4 0 014 4v8" /></svg>
                          <span className="text-gray-800">
                            <strong className="font-semibold">{cListings.length}</strong> listing{cListings.length !== 1 ? 's' : ''} associated with this client
                            {(upcoming > 0 || completed > 0) && (
                              <span className="text-gray-500"> ({upcoming} upcoming, {completed} completed)</span>
                            )}
                          </span>
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="text-gray-800">Client profile, contact info, avatar &amp; company logo</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                          <span className="text-gray-800">Any tasks, notes &amp; activity log entries linked to this client</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                          <span className="text-gray-800">Saved payment methods &amp; the Stripe customer record on this client</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                          <span className="text-gray-800">Future appointment reminders &amp; automation rules targeting this client</span>
                        </li>
                      </ul>
                    );
                  })()}
                  <p className="text-xs text-gray-500 leading-relaxed pt-2 border-t border-gray-100">
                    Listings already delivered to the client will remain in the activity history but will no longer show a client name. Consider keeping the client and just archiving them instead if you want to preserve the relationship for reporting.
                  </p>
                </div>
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowDeleteConfirm(false)}
                    data-testid="delete-client-cancel-btn"
                    className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      const id = editingClient.id;
                      setShowDeleteConfirm(false);
                      if (typeof deleteClient === 'function') {
                        await deleteClient(id);
                      }
                      setShowClientForm(false);
                      setEditingClient(null);
                      resetClientForm();
                    }}
                    data-testid="delete-client-confirm-btn"
                    className="px-4 py-2 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    Delete client permanently
                  </button>
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Client Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="clara-table min-w-full">
            <thead>
              <tr>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">Company</th>
                <th className="text-left px-4 py-3">Listings</th>
              </tr>
            </thead>
            <tbody>
              {safeClients.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No clients found. Add your first client to get started.
                  </td>
                </tr>
              ) : (
                safeClients.map(client => {
                  const clientListings = getClientListings(client.id);
                  const avatarUrl = client.avatar ? clientAvatarUrls[client.avatar] : null;
                  
                  return (
                    <tr
                      key={client.id}
                      onClick={() => handleRowClick(client)}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      data-testid={`client-row-${client.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {avatarUrl ? (
                            <img 
                              src={avatarUrl} 
                              alt={client.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold">
                              {client.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                          )}
                          <div className="font-medium text-gray-900">{client.name}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.email || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{client.company || '-'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                          {clientListings.length} listing{clientListings.length !== 1 ? 's' : ''}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default ClientsPage;
